
import React from 'react';
import { WardrobeItem as WardrobeItemType, UserProfile } from '../types';
import { SearchIcon, CheckCircleIcon, LockIcon } from './icons';
import Spinner from './Spinner';
import { useDynamicStyle } from '../hooks/useDynamicStyle';
import { getMultiCdnUrl } from '../utils/imageUtils';

interface WardrobeItemProps {
    item: WardrobeItemType;
    isActive: boolean;
    isItemLoading: boolean;
    isLoading: boolean;
    isLocked: boolean;
    hasPremiumAccess: boolean;
    onFindSimilar: (item: WardrobeItemType) => void;
    onItemClick: (item: WardrobeItemType) => void;
    t: (key: string, options?: any) => string;
}

export const WardrobeItem: React.FC<WardrobeItemProps> = ({
    item,
    isActive,
    isItemLoading,
    isLoading,
    isLocked,
    hasPremiumAccess,
    onFindSimilar,
    onItemClick,
    t
}) => {
    const styleRef = useDynamicStyle<HTMLDivElement>({ '--item-color': item.defaultColor });

    let itemTitle = `${t(item.nameKey)}\n${t('wardrobe_item_tooltip_category', { category: t(`wardrobe_category_${item.category}` as any, undefined) || item.category })}\n${t('wardrobe_item_tooltip_color', { color: item.defaultColor || 'N/A' })}`;
    if (isLocked) {
        itemTitle = t('wardrobe_item_tooltip_locked', { level: item.requiredMembership === 'premium' ? t('profilePage_membership_premium') : t('profilePage_membership_advanced') });
    }
    const itemImageUrl = item.iconUrl || item.url;
    const fullItemImageUrl = getMultiCdnUrl(itemImageUrl, 'WARDROBE');

    return (
        <div
            title={itemTitle}
            className={`relative flex flex-col text-center rounded-lg transition-shadow duration-300 group ${isActive ? 'ring-2 ring-indigo-500' : 'border border-gray-200 dark:border-gray-700'} ${(isLoading || isItemLoading || isLocked) ? 'opacity-60' : 'hover:shadow-lg'} bg-white dark:bg-gray-800`}
        >
            <button
                onClick={() => onItemClick(item)}
                disabled={isLoading || isItemLoading || isActive || isLocked}
                className="relative w-full aspect-square bg-gray-50 dark:bg-gray-700/50 rounded-t-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 disabled:cursor-not-allowed"
            >
                <img
                    src={fullItemImageUrl}
                    alt={t(item.nameKey)}
                    className="w-full h-full object-contain p-1 transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                />
                {isActive && (
                    <div className="absolute inset-0 bg-indigo-500/30 flex items-center justify-center">
                        <CheckCircleIcon className="w-6 h-6 text-white" />
                    </div>
                )}
                {isItemLoading && (
                    <div className="absolute inset-0 bg-white/70 dark:bg-gray-800/70 flex items-center justify-center">
                        <Spinner className="w-6 h-6" />
                    </div>
                )}
                {isLocked && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center p-2">
                        <LockIcon className="w-8 h-8 text-white/80" />
                    </div>
                )}
            </button>
            <div className="w-full p-1.5 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-1">
                <div className="text-left truncate">
                    <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">{t(item.nameKey)}</p>
                    <div className="flex items-center gap-2 mt-1">
                        {item.defaultColor && (
                            <div
                                ref={styleRef}
                                className="w-3 h-3 rounded-full border border-gray-300 dark:border-gray-600 bg-[var(--item-color)]"
                            ></div>
                        )}
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{t(`wardrobe_category_${item.category}` as any, undefined) || item.category}</p>
                    </div>
                    {item.price && (
                        <p className="text-[10px] font-serif font-semibold text-amber-600 dark:text-amber-500 mt-1">{item.price}</p>
                    )}
                </div>
                <button
                    onClick={() => onFindSimilar(item)}
                    disabled={isLoading || isItemLoading || isLocked || !hasPremiumAccess}
                    className="p-1.5 text-gray-400 hover:text-indigo-600 dark:text-gray-500 dark:hover:text-indigo-400 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={!hasPremiumAccess ? t('feature_locked_premium_generic') : t('outfitStack_findSimilar_aria', { itemName: t(item.nameKey) })}
                >
                    <SearchIcon className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};
